import React from 'react';
import type { ComponentProps } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import {
  Accordion,
  Alert,
  Box,
  Button,
  Chip,
  colors,
  designTokens,
  Grid,
  Panel,
  PanelBody,
  PanelHeader,
  TextLink
} from '@itandi/itandi-bb-ui';
import { PATH as ACCOUNT_PATH } from '~/pages/vendors/settings/accounts';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { useDeals } from '~/hooks/vendors/useDeals';
import { PATH as DEALS_PATH } from '~/pages/vendors/deals';
import { VendorLayoutV2 } from '~/components/common/layouts/VendorLayoutV2';
import Styles from './Page.module.scss';
import { PATH } from '~/pages/vendors/deals/[id]';
import { PATH as ESTIMATIONS_PATH } from '~/pages/vendors/deals/[id]/estimations';
import Loading from '~/components/common/parts/loading/Loading';
import { getDownloadPath } from '~/utils/api/vendors/deals/DealEstimationFile';
import { getDownloadPath as getHistoryDownloadPath } from '~/utils/api/vendors/deals/DealEstimationHistoryFile';
import { useDealEstimations } from '~/hooks/vendors/deals/useDealEstimations';
import { ConstructionTop } from '~/components/common/constructions/top/ConstructionTop';
import { ConstructionList } from '~/components/common/constructions/list/ConstructionList';
import { updateSeq as estimationUpdateSeq } from '~/utils/api/vendors/deals/DealEstimation';
import {
  create as postDealEstimationHistory,
  updateTypeDelete,
  destroy as destroyEstimationHistory,
  updateSeq as historyUpdateSeq
} from '~/utils/api/vendors/deals/DealEstimationHistory';
import type { NskResponse } from '~/models/Api';
import type {
  DealConstruction,
  DealConstructionEdit
} from '~/models/DealConstruction';
import { checkConstructionAddDisabled } from '~/models/DealConstruction';
import { ConstructionAddForm } from '~/components/common/constructions/adding/ConstructionAddForm';
import { ConstructionAddModal } from '~/components/common/constructions/adding/ConstructionAddModal';
import {
  canEstimationRequestStatus,
  IN_CONSTRUCTION,
  IN_ESTIMATION,
  SEND_ESTIMATE
} from '~/models/Status';
import { create } from '~/utils/api/vendors/deals/DealEstimationComplete';
import { isSuccess } from '~/utils/api/api_handler';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import {
  getOriginVendor,
  getSubVendor,
  isSubVendor
} from '~/models/DealVendorCompany';
import { useSessions } from '~/hooks/vendors/useSessions';
import { useDealConstructionItems } from '~/hooks/vendors/deals/useDealConstructionItems';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { ConstructionRequestFooter } from '~/components/common/constructions/ConstructionRequestFooter';
import { useDealEstimationHistories } from '~/hooks/vendors/deals/useDealEstimationHistories';
import { useDealEstimationHeaders } from '~/hooks/vendors/deals/useDealEstimationHeaders';
import {
  create as createHeader,
  destroy,
  update,
  updateRequest
} from '~/utils/api/vendors/deals/DealEstimationHeader';
import { MEDIA_QUERY, useMediaQuery } from '~/hooks/useMediaQuery';
import {
  estimationConstructionContext,
  historyConstructionContext
} from '~/models/DealEstimationHistory';
import { estimationRequestSide } from '~/models/DealEstimationHeader';
import { estimationHeaderStatus } from '~/models/Deal';
import { NoteEditModal } from './NoteEditModal';
import { showNewFeatureForLocal } from '~/utils/FeatureFlagUtil';

export const Page: React.FC = () => {
  const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const { isOpen, handleOpen, handleClose } = useOpenHandler();
  const {
    isOpen: isOpenCreate,
    handleOpen: handleOpenCreate,
    handleClose: handleCloseCreate
  } = useOpenHandler();
  const {
    isOpen: showConfirm,
    handleOpen: handleOpenConfirm,
    handleClose: handleCloseConfirm
  } = useOpenHandler();
  const [isUpdateNoteModalOpen, setIsUpdateNoteModalOpen] =
    React.useState(false);
  const { data: session } = useSessions().index();
  const router = useRouter();
  const id = useIdQueryHandler();
  const { data: deal, mutate: mutateDeal } = useDeals().show(id);
  const { data: dealConstructionItems } = useDealConstructionItems(id);
  const { data: estimationContext } = useDealEstimations().index(id);
  const {
    data: dealEstimationHistoryContext,
    mutate: mutateEstimationHistoryContext
  } = useDealEstimationHistories().index(id);
  const { data: dealEstimationHeaders, mutate: mutateHeader } =
    useDealEstimationHeaders().index(id);
  const requestHeader =
    dealEstimationHeaders?.find((header) => header.status === 'request') ??
    null;
  const { data: requestHistoryContext, isLoading } =
    useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
  const dealEstimationHeaderId =
    dealEstimationHistoryContext?.dealEstimationHeaderId ?? null;
  const vendorCompanyId = session?.vendorCompany.id ?? null;
  React.useEffect(() => {
    if (deal === null) return;
    if (!estimationRequestSide(deal, 'vendor')) {
      router.replace(ESTIMATIONS_PATH(deal.id)).then();
    }
  }, [deal, router]);
  if (
    deal === null ||
    isLoading ||
    session === null ||
    estimationContext === null ||
    dealEstimationHistoryContext === null ||
    dealEstimationHeaders === null ||
    dealConstructionItems === null ||
    vendorCompanyId === null
  ) {
    return <Loading isLoading />;
  }
  const showRequestFooter =
    canEstimationRequestStatus(deal.status) &&
    (dealEstimationHistoryContext?.dealConstructions.length !== 0 ||
      requestHeader !== null);
  const constructionAddDisabled = checkConstructionAddDisabled(
    'estimation',
    requestHeader,
    deal
  );
  const subVendor = getSubVendor(deal);
  // 見積り備考のテンプレートは実施会社を優先する
  const vendorNote = subVendor
    ? subVendor.estimationNoteTemplate
    : getOriginVendor(deal)?.estimationNoteTemplate;
  // ドラフト・リクエストの見積りはそれぞれuniqueなので、どちらかが存在していれば表示する。
  // どちらもなければ、確定している見積りの備考→テンプレートの優先順で表示する。
  const estimationNote =
    dealEstimationHistoryContext.note ??
    requestHeader?.note ??
    estimationContext.note ??
    vendorNote ??
    '';
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細', href: PATH(deal.id), as: Link },
    { label: '見積り作成' }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  const handleClickPreview = (): void => {
    const path = getDownloadPath(deal.id);
    window.open(path);
  };
  const buildHistoryDownloadPath = (): string => {
    // 申請前(draftのheader)
    if (dealEstimationHeaderId) {
      return getHistoryDownloadPath(deal.id, dealEstimationHeaderId);
    }
    // 申請中(requestのheader)
    if (requestHeader) {
      return getHistoryDownloadPath(deal.id, requestHeader.id);
    }
    // どちらもないとき
    return '';
  };
  const handleClickHistoryPreview = (): void => {
    const path = buildHistoryDownloadPath();
    window.open(path);
  };
  const handleClickShowAddModal = (): void => {
    setShowAddModal(true);
  };
  const handleMutateEstimationHistories = async (): Promise<void> => {
    await mutateEstimationHistoryContext?.();
    await mutateDeal?.();
  };
  const handleMutateRequestHeader = async (): Promise<void> => {
    await mutateHeader?.();
  };
  const handleCreate = async (
    submitValue: DealConstructionEdit
  ): Promise<NskResponse<void>> =>
    postDealEstimationHistory(submitValue, deal.id, null, null);
  const handleUpdate = async (
    submitValue: DealConstructionEdit,
    estimationId: number | null,
    historyId: number | null
  ): Promise<NskResponse<void>> => {
    if (estimationId !== null) {
      return postDealEstimationHistory(
        submitValue,
        deal.id,
        estimationId,
        null
      );
    }
    if (historyId === null) {
      throw new Error('historyId cannot be null');
    }
    return postDealEstimationHistory(submitValue, deal.id, null, historyId);
  };
  const handleDestroy = async (
    dealConstruction: DealConstruction
  ): Promise<NskResponse<void>> => {
    if (dealConstruction.type === 'DealBilling') {
      throw new Error('deleteEstimation.type cannot be DealBilling');
    }
    if (dealConstruction.type === 'DealEstimationHistory') {
      if (dealConstruction.operationType === 'add') {
        // そのとき新規追加したものはデータを消す
        return destroyEstimationHistory(deal.id, dealConstruction.id);
      }
      if (dealConstruction.dealEstimation === null) {
        throw new Error('dealEstimation cannot be null');
      }
      // すでに承認されていたものは、申請を通じて削除する
      return updateTypeDelete(deal.id, dealConstruction.dealEstimation.id);
    }
    // すでに承認されていたものは、申請を通じて削除する
    return updateTypeDelete(deal.id, dealConstruction.id);
  };
  const handleUpdateSeq = async (
    newEstimations: DealConstruction[]
  ): Promise<void> => {
    if (newEstimations.length === 0) {
      throw new Error('newEstimations.length cannot be 0');
    }
    if (newEstimations[0].type === 'DealEstimation') {
      await estimationUpdateSeq(newEstimations, deal.id);
      return;
    }
    await historyUpdateSeq(newEstimations, deal.id);
  };
  const handleSubmitComplete = async (): Promise<void> => {
    const result = await create(deal.id);
    if (isSuccess(result)) {
      await router.push(PATH(deal.id));
      toast.success('完了しました');
      return;
    }
    toast.error(`エラーが発生しました。${result.message}`);
  };
  const handleUpdateRequestStatus = async (): Promise<NskResponse<void>> => {
    if (dealEstimationHeaderId === null) {
      throw new Error('dealEstimationHeaderId cannot be null');
    }
    return updateRequest(deal.id, dealEstimationHeaderId);
  };
  const handleUpdateCancelStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return update(deal.id, requestHeader.id, 'cancel');
  };
  const handleClickCreateHeader = async (): Promise<void> => {
    const result = await createHeader(deal.id);
    if (isSuccess(result)) {
      await mutateEstimationHistoryContext?.();
      await mutateDeal?.();
      toast.success('見積りを複製しました');
      handleCloseCreate();
      return;
    }
    handleCloseCreate();
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  const handleClickDestroyHeader = async (): Promise<void> => {
    if (dealEstimationHeaderId === null) {
      throw new Error('dealEstimationHeaderId cannot be null');
    }
    const result = await destroy(deal.id, dealEstimationHeaderId);
    if (isSuccess(result)) {
      await mutateEstimationHistoryContext?.();
      await mutateDeal?.();
      toast.success('編集中の見積りを元に戻しました');
      handleClose();
      return;
    }
    handleClose();
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  if (!windowLoaded) return null;

  return (
    <VendorLayoutV2>
      <DealLayout deal={deal} isVendor items={items}>
        <div className={Styles.EstimateHeader}>
          <h1 className={Styles.EstimateTitle}>見積り作成</h1>
          {estimationHeaderStatus(deal).label !== '' && (
            <Chip
              background={estimationHeaderStatus(deal).color}
              borderColor={null}
              label={estimationHeaderStatus(deal).label}
              maxWidth="100%"
              textColor={colors['itandi-white']}
            />
          )}
        </div>
        {estimationHeaderStatus(deal).description !== '' &&
          deal.status !== IN_ESTIMATION && (
            <Grid paddingX={isSp ? 16 : 0} width={isSp ? '100%' : '60%'}>
              <Alert>{estimationHeaderStatus(deal).description}</Alert>
            </Grid>
          )}
        {estimationHeaderStatus(deal).label === '' &&
          deal.status === SEND_ESTIMATE && (
            <Grid paddingX={isSp ? 16 : 0} width={isSp ? '100%' : '60%'}>
              <Alert variant="info">
                管理会社が見積りを確認しています。施工に関しては、工事完了待ちステータスに進行されるまでお待ちください。
              </Alert>
            </Grid>
          )}
        {estimationHeaderStatus(deal).label === '' &&
          deal.status === IN_CONSTRUCTION && (
            <Grid paddingX={isSp ? 16 : 0} width={isSp ? '100%' : '40%'}>
              <Alert variant="info">
                発注対象となっている施工項目です。施工手続きをお願いします。
              </Alert>
            </Grid>
          )}
        {deal.status === IN_ESTIMATION && (
          <Grid paddingX={isSp ? 16 : 0} width={isSp ? '100%' : '55%'}>
            <Alert>
              初めて見積確認中ステータスに移行する場合、変更前や削除した見積情報の編集履歴を管理会社は確認できません。
            </Alert>
          </Grid>
        )}
        <ConstructionTop
          constructionContext={estimationContext}
          dealEstimationDraftHeader={dealEstimationHistoryContext}
          dealEstimationHistoryContext={historyConstructionContext(
            requestHistoryContext,
            dealEstimationHistoryContext
          )}
          estimationAddDisabled={requestHeader !== null}
          handleClickCreateHeader={
            deal.latestEstimationRejected ? handleOpenCreate : null
          }
          handleClickDestroyHeader={handleOpen}
          handleClickEstimationHistoryPreview={
            // nullの場合はボタンが表示されない
            buildHistoryDownloadPath() === '' ? null : handleClickHistoryPreview
          }
          handleClickEstimationPreview={handleClickPreview}
          handleClickShowAddModal={handleClickShowAddModal}
          readonly={false}
        />
        <ConfirmModal
          confirmType="save"
          handleAccept={handleClickCreateHeader}
          handleCancel={handleCloseCreate}
          isOpen={isOpenCreate}
        >
          棄却された見積りを元に見積りを複製しますか？
        </ConfirmModal>
        <ConfirmModal
          confirmType="save"
          handleAccept={handleClickDestroyHeader}
          handleCancel={handleClose}
          isOpen={isOpen}
        >
          現在編集中の見積りを元に戻しますか？
          <Alert marginTop=".5rem">
            既に反映されている見積りの状態に戻ります
          </Alert>
        </ConfirmModal>
        <div className={Styles.Container}>
          <div className={Styles.EstimateAddForm}>
            <ConstructionAddForm
              dealConstructionItems={dealConstructionItems}
              disabled={constructionAddDisabled}
              handleCreate={handleCreate}
              mutateConstructions={handleMutateEstimationHistories}
            />
          </div>
          <div className={Styles.EstimateList}>
            <ConstructionList
              companyType="vendor"
              constructionContext={estimationConstructionContext(
                requestHistoryContext,
                dealEstimationHistoryContext,
                estimationContext
              )}
              deal={deal}
              dealConstructionItems={dealConstructionItems}
              docType="estimation"
              handleDestroy={handleDestroy}
              handleMutateConstructions={handleMutateEstimationHistories}
              handleUpdate={handleUpdate}
              handleUpdateSeq={requestHeader === null ? handleUpdateSeq : null}
              readonly={false}
              requestHeader={requestHeader}
              showEstimationConfirmedButton={showRequestFooter}
              showHighlight={
                // deal_estimation_headerが存在する時はハイライトを見せる
                dealEstimationHistoryContext.dealConstructions.length > 0 ||
                requestHeader !== null
              }
            />
            {showNewFeatureForLocal() && (
              <Box mt={16}>
                <Panel>
                  <PanelHeader
                    description={
                      <>
                        デフォルトの内容は
                        <TextLink
                          color="link"
                          href={ACCOUNT_PATH}
                          target="_blank"
                        >
                          こちら
                        </TextLink>
                        で設定できます。
                      </>
                    }
                    rightArea={
                      <Button
                        disabled={constructionAddDisabled}
                        onClick={() => setIsUpdateNoteModalOpen(true)}
                        variant="secondary"
                      >
                        編集
                      </Button>
                    }
                    title="備考"
                  />
                  <PanelBody>
                    <Alert mb={designTokens['spacing/m']} variant="info">
                      入力された備考は見積書に出力されます。
                    </Alert>
                    <div
                      className={
                        dealEstimationHistoryContext.noteModified ||
                        requestHeader?.noteModified
                          ? Styles.ModifiedNote
                          : undefined
                      }
                    >
                      <Accordion defaultOpen>{estimationNote}</Accordion>
                    </div>
                  </PanelBody>
                </Panel>
              </Box>
            )}
            {deal.status === IN_ESTIMATION ? (
              <div className={Styles.CompleteButtonArea}>
                {isSubVendor(deal, vendorCompanyId) ? (
                  <Button
                    onClick={handleOpenConfirm}
                    variant="primary"
                    width={isSp ? 'fill' : 'default'}
                  >
                    完了して通知する
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmitComplete}
                    variant="primary"
                    width={isSp ? 'fill' : 'default'}
                  >
                    完了して戻る
                  </Button>
                )}
              </div>
            ) : (
              <>
                {dealEstimationHistoryContext && showRequestFooter && (
                  <ConstructionRequestFooter
                    companyType="vendor"
                    deal={deal}
                    dealEstimationHistoryContext={dealEstimationHistoryContext}
                    handleMutate={handleMutateEstimationHistories}
                    handleMutateRequestHeader={handleMutateRequestHeader}
                    handleUpdateCancelStatus={handleUpdateCancelStatus}
                    handleUpdateRequestStatus={handleUpdateRequestStatus}
                    isAdmin={session.admin}
                    requestHeader={requestHeader}
                  />
                )}
              </>
            )}
            <ConfirmModal
              actionText="完了"
              confirmType="save"
              handleAccept={handleSubmitComplete}
              handleCancel={handleCloseConfirm}
              isOpen={showConfirm}
            >
              完了してよろしいですか？
            </ConfirmModal>
          </div>
        </div>
        {showAddModal && (
          <ConstructionAddModal
            dealConstructionItems={dealConstructionItems}
            disabled={constructionAddDisabled}
            handleClose={(): void => {
              setShowAddModal(false);
            }}
            handleCreate={handleCreate}
            mutateConstructions={handleMutateEstimationHistories}
          />
        )}
        {isUpdateNoteModalOpen && (
          <NoteEditModal
            deal={deal}
            defaultNote={estimationNote}
            onClose={() => setIsUpdateNoteModalOpen(false)}
          />
        )}
      </DealLayout>
    </VendorLayoutV2>
  );
};
